import { injectStrict } from '@/injection/injectStrict.ts';
import { ApiKey } from '@/injection/keys.ts';
import { ApiRequestParams } from '@/types/api.type.ts';
import {
    User,
    UserCreateForm,
    UserCreateFormErrors,
    UserCreateFormErrorsSchema,
    UserNotificationSetting,
    UserNotificationSettingsSchema,
    UserNotificationSettingsUpdateForm,
    UserNotificationSettingsUpdateFormErrors,
    UserNotificationSettingsUpdateFormErrorsSchema,
    UserSchema,
    UsersPaginator,
    UsersPaginatorSchema,
    UsersRequestFilter,
    UserUpdateForm,
    UserUpdateFormErrors,
    UserUpdateFormErrorsSchema,
} from '@/types/user.type.ts';
import { DealerListItem, DealersSchema } from '@/types/dealer.type.ts';

class UserRepository
{
    private $Api: any;

    constructor() {
        this.$Api = injectStrict(ApiKey);
    }

    public getUser(id: number): Promise<User>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getUser', requestParams)
                .then(({ data }: any) => {
                    const parsedUser = UserSchema.parse(data);
                    const user: User = parsedUser as unknown as User;

                    resolve(user);
                });
        });
    }

    public getUsers(usersRequestFilter?: UsersRequestFilter, oAbortController?: AbortController): Promise<UsersPaginator>
    {
        const requestParams: ApiRequestParams = {
            query: usersRequestFilter,
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getUsers', requestParams, null, oAbortController)
                .then(({ data }: any) => {
                    const parsedUsersPaginator = UsersPaginatorSchema.parse(data);
                    const usersPaginator = parsedUsersPaginator as unknown as UsersPaginator;

                    resolve(usersPaginator);
                });
        });
    }

    public createUser(userCreateForm: UserCreateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            requestBody: userCreateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('createUser', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedCreateUserErrors = UserCreateFormErrorsSchema.parse(errors);
                        const createUserErrors: UserCreateFormErrors = parsedCreateUserErrors as unknown as UserCreateFormErrors;

                        reject(createUserErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public updateUser(id: number, userUpdateForm: UserUpdateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: userUpdateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('updateUser', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedUpdateUserErrors = UserUpdateFormErrorsSchema.parse(errors);
                        const updateUserErrors: UserUpdateFormErrors = parsedUpdateUserErrors as unknown as UserUpdateFormErrors;

                        reject(updateUserErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public activateUser(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('activateUser', requestParams)
                .then(() => resolve());
        });
    }

    public deactivateUser(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('deactivateUser', requestParams)
                .then(() => resolve());
        });
    }

    public approveUser(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('approveUser', requestParams)
                .then(() => resolve());
        });
    }

    public rejectUser(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('rejectUser', requestParams)
                .then(() => resolve());
        });
    }

    public getUserDealers(id: number): Promise<DealerListItem[]>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getUserDealers', requestParams)
                .then(({ data }: any) => {
                    const parsedDealers = DealersSchema.parse(data);
                    const dealers: DealerListItem[] = parsedDealers as unknown as DealerListItem[];

                    resolve(dealers);
                });
        });
    }

    public changeUserCurrentDealer(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('changeCurrentDealer', requestParams)
                .then(() => resolve());
        });
    }

    public getUserNotificationSettings(): Promise<UserNotificationSetting[]>
    {
        return new Promise((resolve) => {
            this.$Api.callNew('getUserNotificationSettings')
                .then(({ data }: any) => {
                    const parsedSettings = UserNotificationSettingsSchema.parse(data);
                    const settings: UserNotificationSetting[] = parsedSettings as unknown as UserNotificationSetting[];

                    resolve(settings);
                });
        });
    }

    public updateUserNotificationSettings(form: UserNotificationSettingsUpdateForm): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            requestBody: form,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('updateUserNotificationSettings', requestParams)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedUpdateErrors = UserNotificationSettingsUpdateFormErrorsSchema.parse(errors);
                        const updateErrors: UserNotificationSettingsUpdateFormErrors = parsedUpdateErrors as unknown as UserNotificationSettingsUpdateFormErrors;

                        reject(updateErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }
}

export default UserRepository;
